import { supabase } from '../lib/supabase';

class UserManagementService {
  // Get all users (admin only)
  async getAllUsers() {
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*')?.order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Create a new user (admin only)
  async createUser(userData) {
    try {
      const { email, password, full_name, role, status } = userData;

      // Create user in auth.users
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name,
            role
          }
        }
      });

      if (authError) {
        throw new Error(authError.message);
      }

      if (!authData?.user) {
        throw new Error('Failed to create user');
      }

      // The profile will be created automatically via trigger
      // But we'll wait a moment and then fetch it to return
      await new Promise(resolve => setTimeout(resolve, 1000));

      const { data: profileData, error: profileError } = await supabase?.from('profiles')?.select('*')?.eq('id', authData?.user?.id)?.single();

      if (profileError) {
        console.error('Error fetching created profile:', profileError);
        // Return basic data if profile fetch fails
        return {
          id: authData?.user?.id,
          email,
          full_name,
          role,
          status: status || 'active',
          created_at: new Date()?.toISOString()
        };
      }

      return profileData;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Update user information
  async updateUser(userId, userData) {
    try {
      const { email, full_name, role, status } = userData;

      const { data, error } = await supabase?.from('profiles')?.update({
          email,
          full_name,
          role,
          status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Update user status
  async updateUserStatus(userId, status) {
    try {
      const { data, error } = await supabase?.from('profiles')?.update({
          status,
          updated_at: new Date()?.toISOString()
        })?.eq('id', userId)?.select()?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  // Delete a user (admin only)
  async deleteUser(userId) {
    try {
      // First delete the profile
      const { error: profileError } = await supabase?.from('profiles')?.delete()?.eq('id', userId);

      if (profileError) {
        throw new Error(profileError.message);
      }

      // Then delete from auth.users (this requires admin privileges)
      // Note: This might require a server-side function with service role key
      // For now, we'll just delete the profile
      
      return true;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Get current user profile
  async getCurrentUser() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError) {
        throw new Error(authError.message);
      }

      if (!user) {
        return null;
      }

      const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('id', user?.id)?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  // Update last login timestamp
  async updateLastLogin() {
    try {
      const { error } = await supabase?.rpc('update_last_login');
      
      if (error) {
        console.error('Error updating last login:', error);
      }
    } catch (error) {
      console.error('Error updating last login:', error);
    }
  }

  // Get user by ID
  async getUserById(userId) {
    try {
      const { data, error } = await supabase?.from('profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  }
}

export const userManagementService = new UserManagementService();